/**
 *  Méga-menu par univers : bascule des panneaux, clavier, chargement des images.
 *
 *  Trois écarts volontaires avec la version d'origine du thème :
 *
 *  1. **Les images ne sont plus dévoilées au chargement de la page.** L'origine appelait
 *     `lazySizes.loader.unveil()` sur TOUTES les images au `load`, ce qui annulait le
 *     chargement paresseux : une dizaine de visuels produit étaient téléchargés sur chaque
 *     page du site, pour un panneau que la plupart des visiteurs n'ouvrent jamais. On ne
 *     dévoile plus qu'à la première activation, panneau par panneau.
 *  2. **Le composant écoute le clavier.** L'origine n'écoutait que `mouseover` : le panneau
 *     était inatteignable sans souris.
 *  3. **La bascule attend une intention.** Sans délai, traverser la barre latérale allume
 *     successivement chaque univers.
 */

// Assez court pour que la bascule d'un univers à l'autre suive la souris, assez long
// pour ne pas allumer chaque univers quand on ne fait que traverser la colonne.
const DELAI_INTENTION = 45;

/*  La retenue des images est posée dans `layout/theme.liquid`, avant lazySizes : un
 *  script différé arriverait trop tard, la bibliothèque ayant déjà dévoilé le panneau
 *  actif. Ici on se contente de LEVER cette retenue à la première ouverture.
 */


if (!customElements.get('mega-menu-sidebar')) {
  class MegaMenuSidebar extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('.mega-menu-sidebar--button'));
      this.content = Array.from(this.querySelectorAll('.mega-menu-sidebar--content-inner'));
      this.accroches = Array.from(this.querySelectorAll('.mega-menu-sidebar--tagline'));
      this.minuteur = null;
      this.devoiles = new WeakSet();

      this.buttons.forEach((button, i) => {
        button.addEventListener('mouseenter', () => this.programmer(i));
        button.addEventListener('mouseleave', () => this.annuler());
        // Le focus bascule sans délai : au clavier, l'intention est déjà exprimée.
        button.addEventListener('focus', () => this.activer(i));
        button.addEventListener('keydown', (event) => this.auClavier(event, i));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.fermer();
      });

      // Le panneau affiché par défaut charge ses images dès que le visiteur survole
      // l'ENTRÉE DE MENU, pas quand la souris atteint le composant : le panneau s'ouvre
      // au survol de « Boutique », et attendre l'entrée dans la zone des cartes laissait
      // les images sous leur voile de chargement pendant tout le temps de lecture.
      // Surtout pas sur `transitionend` du panneau parent : cet évènement se déclenche
      // dès la mise en page initiale et rechargerait le panneau sur chaque page.
      const actif = this.content.findIndex((c) => c.classList.contains('active'));
      const declencheur = this.closest('.menu-item-has-megamenu') || this;
      declencheur.addEventListener('mouseenter', () => this.siVisible(actif), { once: true });
      declencheur.addEventListener('focusin', () => this.siVisible(actif), { once: true });
      declencheur.addEventListener('touchstart', () => this.siVisible(actif), { once: true, passive: true });
    }

    disconnectedCallback() {
      this.annuler();
    }

    siVisible(index) {
      if (index >= 0) this.devoiler(this.content[index]);
      this.content.forEach((p) => this.marquerPastilles(p));
    }

    programmer(i) {
      this.annuler();
      this.minuteur = setTimeout(() => this.activer(i), DELAI_INTENTION);
    }

    annuler() {
      if (this.minuteur) {
        clearTimeout(this.minuteur);
        this.minuteur = null;
      }
    }

    activer(i) {
      this.annuler();
      this.content.forEach((panneau, index) => {
        panneau.classList.toggle('active', index === i);
      });
      this.buttons.forEach((bouton, index) => {
        const estActif = index === i;
        bouton.classList.toggle('active', estActif);
        bouton.setAttribute('aria-selected', estActif ? 'true' : 'false');
      });
      // L'accroche suit son univers. On l'apparie par l'identifiant du panneau et non par
      // le rang : un univers dépourvu d'accroche ne décale pas les suivantes.
      const panneau = this.content[i];
      this.accroches.forEach((accroche) => {
        accroche.classList.toggle('active', accroche.dataset.univers === (panneau && panneau.id));
      });
      this.devoiler(this.content[i]);
    }


    /**
     *  Marque la pastille de statut du panneau.
     *
     *  Les pastilles sont écrites par `custom.js` depuis le métafield `custom.badges`,
     *  dans l'ordre du métafield. La planche montre « ★ BEST-SELLER » ; sans
     *  intervention, une fiche portant AWARD WINNER en premier afficherait l'étoile sur
     *  le mauvais libellé, la règle du menu n'en gardant qu'une. On remonte donc la
     *  pastille best-seller en tête et on la marque, l'étoile étant dessinée en CSS.
     *
     *  Fait ici et pas dans `custom.js`, qui sert aussi aux collections, à la fiche et
     *  au tiroir panier.
     */
    marquerPastilles(panneau) {
      if (!panneau) return;
      panneau.querySelectorAll('.product-card-info .badges').forEach((groupe) => {
        const pastilles = Array.from(groupe.querySelectorAll('.badge'));
        const statut = pastilles.find((b) => /best.?seller/i.test(b.textContent));
        if (statut) {
          if (statut !== pastilles[0]) groupe.insertBefore(statut, pastilles[0]);
          statut.setAttribute('data-etoile', '');
        }
      });
    }

    /** Charge les images d'un panneau, une seule fois. */
    devoiler(panneau) {
      if (!panneau || this.devoiles.has(panneau)) return;
      this.devoiles.add(panneau);
      // Lève la retenue posée plus haut : à partir d'ici, lazySizes a le droit d'agir.
      const conteneur = this.closest('.mega-menu-container');
      if (conteneur) conteneur.dataset.ouvert = '1';
      const images = panneau.querySelectorAll('img.lazyload, img.lazyloading');
      images.forEach((image) => {
        // On promeut nous-mêmes les attributs différés plutôt que de repasser la main à
        // lazySizes : notre retenue a annulé son cycle, elle considère ces images comme
        // déjà traitées et ne reposerait pas la classe finale. Elles resteraient alors
        // sous le voile `filter: blur(5px)` que `.lazyload` applique (app.css).
        // `sizes` AVANT `srcset` : le navigateur choisit la source à la pose du srcset.
        if (image.dataset.sizes === 'auto') {
          image.sizes = Math.round(image.getBoundingClientRect().width) + 'px';
        }
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        if (image.dataset.src) image.src = image.dataset.src;
        image.classList.remove('lazyload', 'lazyloading');
        image.classList.add('lazyloaded');
      });
    }

    auClavier(event, i) {
      const dernier = this.buttons.length - 1;
      let cible = null;
      if (event.key === 'ArrowDown') cible = i === dernier ? 0 : i + 1;
      else if (event.key === 'ArrowUp') cible = i === 0 ? dernier : i - 1;
      else if (event.key === 'Home') cible = 0;
      else if (event.key === 'End') cible = dernier;
      if (cible === null) return;
      event.preventDefault();
      this.buttons[cible].focus();
    }

    /** Échap ferme le panneau et rend le focus à l'entrée de menu parente. */
    fermer() {
      const entree = this.closest('.menu-item-has-megamenu');
      const declencheur = entree && entree.querySelector(':scope > a');
      // Rendre le focus au parent suffirait à rouvrir le panneau, la feuille de style
      // l'ouvrant précisément au focus de ce lien. On marque donc l'entrée fermée le temps
      // que le visiteur reparte, sinon Échap n'aurait aucun effet visible.
      if (entree) {
        entree.dataset.ferme = '';
        const lever = () => delete entree.dataset.ferme;
        entree.addEventListener('mouseenter', lever, { once: true });
        if (declencheur) declencheur.addEventListener('blur', lever, { once: true });
      }
      if (declencheur) declencheur.focus();
      if (document.activeElement && this.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }
  }
  customElements.define('mega-menu-sidebar', MegaMenuSidebar);
}
